// FilterBar (spec/dashboard.md section 2.4): rule chips with a popover editor, saved filters per grid per user, the
// whole filter in the URL (`?f=`) so it can be shared; a half-built rule survives the popover closing (Q-D-19).
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Button } from './Button';
import { Chip } from './Chip';
import { Popover } from './Popover';
import { Plus } from './icons';
import { Select, TextInput } from './inputs';

export type FilterType = 'string' | 'number' | 'bool' | 'enum' | 'date' | 'tag';
export type Comparator = 'eq' | 'ne' | 'gt' | 'ge' | 'lt' | 'le' | 'contains' | 'starts' | 'empty' | 'notEmpty';

export interface FilterField {
  key: string;
  label: string;
  type: FilterType;
  group: string;
  options?: { value: string; label: string }[];
}

export interface FilterRule {
  field: string;
  op: Comparator;
  value: string;
}

export const comparatorsByType: Record<FilterType, Comparator[]> = {
  string: ['contains', 'eq', 'ne', 'starts', 'empty', 'notEmpty'],
  number: ['eq', 'ne', 'gt', 'ge', 'lt', 'le', 'empty'],
  bool: ['eq'],
  enum: ['eq', 'ne', 'empty'],
  date: ['ge', 'le', 'eq'],
  tag: ['eq'],
};

export const comparatorLabel: Record<Comparator, string> = { eq: '=', ne: '≠', gt: '>', ge: '≥', lt: '<', le: '≤', contains: 'contains', starts: 'starts with', empty: 'is empty', notEmpty: 'is not empty' };

export function encodeFilter(rules: FilterRule[]): string | null {
  if (rules.length === 0) return null;
  const json = JSON.stringify(rules);
  return btoa(unescape(encodeURIComponent(json))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function decodeFilter(text: string | null): FilterRule[] {
  if (!text) return [];
  try {
    const b64 = text.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(escape(atob(b64)));
    const parsed = JSON.parse(json) as FilterRule[];
    return Array.isArray(parsed) ? parsed.filter((r) => r && typeof r.field === 'string') : [];
  } catch {
    return [];
  }
}

/** The filter rules kept in the URL under `?f=`. */
export function useUrlFilter(): [FilterRule[], (rules: FilterRule[]) => void] {
  const [params, setParams] = useSearchParams();
  const rules = useMemo(() => decodeFilter(params.get('f')), [params]);
  const set = useCallback(
    (next: FilterRule[]) => {
      const p = new URLSearchParams(params);
      const enc = encodeFilter(next);
      if (enc) p.set('f', enc);
      else p.delete('f');
      setParams(p, { replace: true });
    },
    [params, setParams],
  );
  return [rules, set];
}

/** Applies a rule set to loaded rows (the owner decides which rules the API already evaluated). */
export function applyRules<T>(rows: T[], rules: FilterRule[], valueOf: (row: T, field: string) => unknown): T[] {
  if (rules.length === 0) return rows;
  return rows.filter((row) =>
    rules.every((r) => {
      const v = valueOf(row, r.field);
      const s = v === null || v === undefined ? '' : Array.isArray(v) ? v.map(String).join(',') : String(v);
      const n = typeof v === 'number' ? v : Number(s);
      const rv = r.value;
      switch (r.op) {
        case 'eq':
          return Array.isArray(v) ? v.map(String).includes(rv) : s === rv || (typeof v === 'boolean' && String(v) === rv);
        case 'ne':
          return s !== rv;
        case 'gt':
          return n > Number(rv);
        case 'ge':
          return n >= Number(rv);
        case 'lt':
          return n < Number(rv);
        case 'le':
          return n <= Number(rv);
        case 'contains':
          return s.toLowerCase().includes(rv.toLowerCase());
        case 'starts':
          return s.toLowerCase().startsWith(rv.toLowerCase());
        case 'empty':
          return v === null || v === undefined || s === '' || (Array.isArray(v) && v.length === 0);
        case 'notEmpty':
          return !(v === null || v === undefined || s === '' || (Array.isArray(v) && v.length === 0));
      }
    }),
  );
}

interface SavedFilter {
  name: string;
  rules: FilterRule[];
}

function loadSaved(id: string): SavedFilter[] {
  try {
    const raw = localStorage.getItem(`hue.filters:${id}`);
    return raw ? (JSON.parse(raw) as SavedFilter[]) : [];
  } catch {
    return [];
  }
}

export interface FilterBarProps {
  id: string;
  fields: FilterField[];
  rules: FilterRule[];
  onChange: (rules: FilterRule[]) => void;
}

export function FilterBar({ id, fields, rules, onChange }: FilterBarProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [draft, setDraft] = useState<FilterRule>({ field: fields[0]?.key ?? '', op: 'contains', value: '' });
  const [saved, setSaved] = useState<SavedFilter[]>(() => loadSaved(id));
  const [savedAnchor, setSavedAnchor] = useState<HTMLElement | null>(null);
  const [saveName, setSaveName] = useState('');

  const fieldOf = (key: string) => fields.find((f) => f.key === key);
  const groups = useMemo(() => Array.from(new Set(fields.map((f) => f.group))), [fields]);

  const persistSaved = (next: SavedFilter[]) => {
    setSaved(next);
    try {
      localStorage.setItem(`hue.filters:${id}`, JSON.stringify(next));
    } catch {
      // ignore
    }
  };

  const commitDraft = () => {
    const field = fieldOf(draft.field);
    if (!field) return;
    const needsValue = draft.op !== 'empty' && draft.op !== 'notEmpty' && field.type !== 'bool';
    if (needsValue && draft.value === '') return;
    const next = [...rules];
    if (editingIndex !== null) next[editingIndex] = draft;
    else next.push(draft);
    onChange(next);
    setAnchor(null);
    setEditingIndex(null);
  };

  const openFor = (el: HTMLElement, index: number | null) => {
    setEditingIndex(index);
    if (index !== null) setDraft(rules[index]!);
    setAnchor(el);
  };

  const currentField = fieldOf(draft.field);
  const comparators = currentField ? comparatorsByType[currentField.type] : [];

  return (
    <div className="filterbar" role="group" aria-label="Filters">
      {rules.map((r, i) => {
        const f = fieldOf(r.field);
        return (
          <Chip key={`${r.field}-${i}`} tone="accent" onRemove={() => onChange(rules.filter((_, j) => j !== i))} removeLabel={`Remove filter ${f?.label ?? r.field}`}>
            <button type="button" className="btn btn-ghost btn-s" style={{ padding: 0 }} onClick={(e) => openFor(e.currentTarget, i)}>
              {f?.label ?? r.field} {comparatorLabel[r.op]} {r.op === 'empty' || r.op === 'notEmpty' ? '' : f?.type === 'enum' ? (f.options?.find((o) => o.value === r.value)?.label ?? r.value) : r.value}
            </button>
          </Chip>
        );
      })}
      <Button size="s" variant="ghost" onClick={(e) => openFor(e.currentTarget, null)} icon={<Plus aria-hidden="true" />}>
        Add filter
      </Button>
      {(rules.length > 0 || saved.length > 0) && (
        <Button size="s" variant="ghost" onClick={(e) => setSavedAnchor(e.currentTarget)}>
          Saved filters{saved.length ? ` (${saved.length})` : ''}
        </Button>
      )}
      {rules.length > 0 && (
        <Button size="s" variant="ghost" onClick={() => onChange([])}>
          Clear
        </Button>
      )}
      <Popover anchor={anchor} open={anchor !== null} onClose={() => setAnchor(null)} label={editingIndex === null ? 'Add filter' : 'Edit filter'}>
        <div className="stack">
          <label className="field-label" htmlFor={`${id}-ff`}>
            Field
          </label>
          <Select
            id={`${id}-ff`}
            size="s"
            value={draft.field}
            onChange={(e) => {
              const f = fieldOf(e.target.value);
              setDraft({ field: e.target.value, op: f ? comparatorsByType[f.type][0]! : 'eq', value: f?.type === 'bool' ? 'true' : '' });
            }}
          >
            {groups.map((g) => (
              <optgroup key={g} label={g}>
                {fields.filter((f) => f.group === g).map((f) => (
                  <option key={f.key} value={f.key}>
                    {f.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
          <label className="field-label" htmlFor={`${id}-fo`}>
            Comparator
          </label>
          <Select id={`${id}-fo`} size="s" value={draft.op} onChange={(e) => setDraft({ ...draft, op: e.target.value as Comparator })}>
            {comparators.map((c) => (
              <option key={c} value={c}>
                {comparatorLabel[c]}
              </option>
            ))}
          </Select>
          {draft.op !== 'empty' && draft.op !== 'notEmpty' && currentField && (
            <>
              <label className="field-label" htmlFor={`${id}-fv`}>
                Value
              </label>
              {currentField.type === 'bool' ? (
                <Select id={`${id}-fv`} size="s" value={draft.value || 'true'} onChange={(e) => setDraft({ ...draft, value: e.target.value })}>
                  <option value="true">true</option>
                  <option value="false">false</option>
                </Select>
              ) : currentField.type === 'enum' && currentField.options ? (
                <Select id={`${id}-fv`} size="s" value={draft.value} onChange={(e) => setDraft({ ...draft, value: e.target.value })}>
                  <option value="">—</option>
                  {currentField.options.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              ) : (
                <TextInput
                  id={`${id}-fv`}
                  size="s"
                  type={currentField.type === 'number' ? 'number' : currentField.type === 'date' ? 'datetime-local' : 'text'}
                  value={draft.value}
                  onChange={(e) => setDraft({ ...draft, value: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitDraft();
                    }
                  }}
                />
              )}
            </>
          )}
          <div className="row">
            <Button size="s" variant="primary" onClick={commitDraft}>
              {editingIndex === null ? 'Add rule' : 'Update rule'}
            </Button>
            <Button size="s" onClick={() => setAnchor(null)}>
              Cancel
            </Button>
          </div>
        </div>
      </Popover>
      <Popover anchor={savedAnchor} open={savedAnchor !== null} onClose={() => setSavedAnchor(null)} label="Saved filters">
        <div className="stack">
          {saved.length === 0 && <span className="muted small">No saved filters yet.</span>}
          {saved.map((s) => (
            <div key={s.name} className="row">
              <Button
                size="s"
                variant="ghost"
                onClick={() => {
                  onChange(s.rules);
                  setSavedAnchor(null);
                }}
              >
                {s.name}
              </Button>
              <span className="spacer" />
              <Button size="s" variant="ghost" aria-label={`Delete saved filter ${s.name}`} onClick={() => persistSaved(saved.filter((x) => x.name !== s.name))}>
                Delete
              </Button>
            </div>
          ))}
          {rules.length > 0 && (
            <div className="row">
              <TextInput size="s" aria-label="Saved filter name" placeholder="Name this filter" value={saveName} onChange={(e) => setSaveName(e.target.value)} />
              <Button
                size="s"
                variant="primary"
                disabled={saveName.trim() === ''}
                onClick={() => {
                  persistSaved([...saved.filter((s) => s.name !== saveName.trim()), { name: saveName.trim(), rules }]);
                  setSaveName('');
                }}
              >
                Save
              </Button>
            </div>
          )}
        </div>
      </Popover>
    </div>
  );
}
